import { Breadcrumbs } from '@material-tailwind/react';
import { useQuery } from '@tanstack/react-query';
import bannerImg from 'assets/images/Building-Good-in-2020-scaled.jpg'
import Section from 'components/Section'
import AspectRatioDiv from 'components/utils/AspectRatioDiv'
import Spinner from 'components/utils/Spinner';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import TextTruncate from 'react-text-truncate';
import axios from 'utils/axios';
import resolveImgUrl from 'utils/resolveImgUrl';
import { Blog } from 'utils/types';

const BlogShow = () => {
    const { slug } = useParams();

    const {data, status, error} = useQuery(['blogs', {slug}], async()=>{
        const {data} = await axios.get(`blogs/${slug}`);
        return data as Blog;
    })

    const {data: relatedBlogs, status: relatedBlogsStatus, error: relatedBlogsError} = useQuery(['blogs', {limit: 3, exclude : data?.id, category: data?.category.id}], async()=>{
        const {data : _data} = await axios.get<[Blog[], number]>(`blogs`, {params: {limit: 3, category: data?.category.id, exclude: data?.id}});
        return _data[0]
    }, {enabled: !!data?.category.id})

    const {data: recentBlogs, status: recentBlogsStatus, error: recentBlogsError} = useQuery(['blogs', {limit: 3, exclude : data?.id}], async()=>{
        const {data : _data} = await axios.get<[Blog[], number]>(`blogs`, {params: {limit: 3, exclude: data?.id}});
        return _data[0]
    }, {enabled: !!data?.id})

    if(status === 'loading' || relatedBlogsStatus === 'loading' || recentBlogsStatus === 'loading')
        return <div className='min-h-screen grid place-content-center'>
            <Spinner twWidthHeight="w-20 h-20"></Spinner>
        </div>
    if(status === 'error' || relatedBlogsStatus === 'error' || recentBlogsStatus === 'error')
        return <div className='min-h-screen grid place-content-center'>
            <div className="flex flex-col items-center gap-y-2">
                <i className="fas fa-circle-exclamation text-3xl"></i>
                <div className="text-8xl font-semibold">
                    {/* @ts-ignore */}
                    {error.response?.status || relatedBlogsError.response?.status || recentBlogsError.response?.status || "something went wrong"}
                </div>
                <Link to="/" className="text-blue-500 hover:underline">
                    Back to home
                </Link>
            </div>
        </div>
    return <div>
        <div className='absolute top-0 left-0 w-full z-[-1]'>
            <AspectRatioDiv className="pt-[100%] md:pt-[60%] lg:pt-[40%] xl:pt-[35%] group">
                <div className='absolute  top-0 left-0 w-1/6 h-full bg-gradient-to-r from-[#000000] to-[#00000000]'></div>
                <div className='absolute  top-0 right-0 w-1/6 h-full bg-gradient-to-l from-[#000000] to-[#00000000]'></div>

                <img src={resolveImgUrl(data.imgUrl)} className="w-full absolute top-0 left-0 object-cover z-[-2] object-center h-full" alt="" />
            </AspectRatioDiv>
        </div>
        <AspectRatioDiv className="pt-[100%] md:pt-[60%] lg:pt-[40%] xl:pt-[35%] group"></AspectRatioDiv>
        <Section isContainer className='grid xl:grid-cols-[4.5rem,1fr,17rem] gap-8 z-[2] mt-5 mb-12'>
            <div className='shadow-around w-full grid-rows-[repeat(5,2.5rem)] h-max p-3 gap-3 sticky top-20 xl:grid hidden'>
                <div className='grid place-content-center'>
                    <i className="fa-solid fa-share-nodes text-2xl text-gray-700"></i>
                </div>
                <a target="_blank" className="block h-full w-full" href={`https://www.facebook.com/sharer/sharer.php?u=${process.env.REACT_APP_BASE_URL_NAME?.replaceAll("/", "%2F").replaceAll(":", "%3A")}%2Fblogs%2F${data.slug}&amp;src=sdkpreparse`}>
                  <div 
                    className='bg-blue-500 h-full w-full grid place-content-center text-white'
                  >
                    <i className="fa-brands fa-facebook-f"></i>
                  </div>
                </a>
                <a target="_blank" className="block h-full w-full" href={`https://twitter.com/intent/tweet?text=Check%20out%20this%20cool%20story%20i%20found%20%F0%9F%91%89%20${process.env.REACT_APP_BASE_URL_NAME?.replaceAll(":", "%3A")}/blogs/${data.slug}`}>
                  <div className='bg-sky-500 h-full w-full grid place-content-center text-white'>
                      <i className="fa-brands fa-twitter"></i>
                  </div>
                </a>
                <a target="_blank" className="block h-full w-full" href={`https://api.whatsapp.com/send?text=Check%20out%20this%20cool%20story%20i%20found%20%F0%9F%91%89%20${process.env.REACT_APP_BASE_URL_NAME?.replaceAll("/", "%2F").replaceAll(":", "%3A")}%2Fblogs%2F${data.slug}`} data-action="share/whatsapp/share">
                  <div className='bg-green-500 h-full w-full grid place-content-center text-white'>
                      <i className="fa-brands fa-whatsapp"></i>
                  </div>
                </a>
                <a target="_blank" className="block h-full w-full" href={`https://www.linkedin.com/shareArticle?mini=true&url=${process.env.REACT_APP_BASE_URL_NAME?.replaceAll(":", "%3A")}/blogs/${data.slug}`}>
                  <div className='bg-sky-500 h-full w-full grid place-content-center text-white'>
                      <i className="fa-brands fa-linkedin-in"></i>
                  </div>
                </a>
            </div>
            <div className='w-full -mt-20'>
                <div className='bg-white px-6 lg:px-10 xl:px-16 py-6 lg:py-12 shadow-around'>
                    <div className=' flex-col gap-y-4 flex w-full'>
                        <div className='breadcrumb-container'>  
                        <Breadcrumbs>
                          <Link to='/'>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                            </svg>
                          </Link>
                          <Link to='/blogs'>
                              Blogs
                          </Link>
                          <Link to={'/blogs/'+data.slug} dangerouslySetInnerHTML={{__html:data.slug.split('-').join('-&shy;')}}>
                          </Link>
                        </Breadcrumbs>
                        </div>
                        <span className='px-2 py-1 rounded-full text-sm w-max text-white' style={{backgroundColor : `#${data.category.color}`}}>{data.category.title}</span>
                        <h2 className='font-semibold text-4xl'>{data.title}</h2>
                        <div className='flex flex-col sm:flex-row sm:items-center gap-3 relative'>
                            <div className='sm:hidden flex gap-2 items-center'>
                              <div className='flex items-center gap-1'>
                                  <i className="fa-regular fa-calendar"></i>
                                  <span className='text-xs'>{data.timeAgo}</span>
                              </div>
                              <span className='border border-gray-500 h-4'></span>                              
                              <div className='flex items-center gap-1'>
                                  <i className="fa-regular fa-clock"></i>
                                  <span className='text-xs'>{data.readingTime} menit</span>
                              </div>
                            </div>
                            <div className='flex items-center gap-1'>
                                <div className='w-6 h-6 rounded-full'>
                                  {!data.submittedBy.profilePicture ?
                                  <div className='w-full h-full rounded-full bg-gray-500'></div>
                                  : <img src={resolveImgUrl(data.submittedBy.profilePicture)} className='w-full h-full rounded-full object-cover' alt="" />}
                                </div>
                                <span className='text-xs'>{data.submittedBy.name}</span>
                            </div>
                            <div className='sm:flex items-center gap-1 hidden'>
                                <i className="fa-regular fa-calendar"></i>
                                <span className='text-xs'>{data.timeAgo}</span>
                            </div>
                            <div className='sm:flex items-center gap-1 hidden'>
                                <i className="fa-regular fa-clock"></i>
                                <span className='text-xs'>{data.readingTime} menit</span>
                            </div>
                        </div>
                    </div>
                    <div className='mt-8 flex flex-col gap-y-4 prose sm:prose-sm md:prose-base xl:prose-lg' dangerouslySetInnerHTML={{__html: data.description}}></div>
                </div>
                <div className='shadow-around w-full my-10 h-16 flex xl:hidden p-3 gap-3'>
                    <div className='grid place-content-center'>
                        <i className="fa-solid fa-share-nodes text-2xl text-gray-700"></i>
                    </div>
                    <a target="_blank" className="block h-full w-full" href={`https://www.facebook.com/sharer/sharer.php?u=${process.env.REACT_APP_BASE_URL_NAME?.replaceAll("/", "%2F").replaceAll(":", "%3A")}%2Fblogs%2F${data.slug}&amp;src=sdkpreparse`}>
                      <div 
                        className='bg-blue-500 h-full w-full grid place-content-center text-white'
                      >
                        <i className="fa-brands fa-facebook-f"></i>
                      </div>
                    </a>
                    <a target="_blank" className="block h-full w-full" href={`https://twitter.com/intent/tweet?text=Check%20out%20this%20cool%20story%20i%20found%20%F0%9F%91%89%20${process.env.REACT_APP_BASE_URL_NAME?.replaceAll(":", "%3A")}/blogs/${data.slug}`}>
                      <div className='bg-sky-500 h-full w-full grid place-content-center text-white'>
                          <i className="fa-brands fa-twitter"></i>
                      </div>
                    </a>
                    <a target="_blank" className="block h-full w-full" href={`https://api.whatsapp.com/send?text=Check%20out%20this%20cool%20story%20i%20found%20%F0%9F%91%89%20${process.env.REACT_APP_BASE_URL_NAME?.replaceAll("/", "%2F").replaceAll(":", "%3A")}%2Fblogs%2F${data.slug}`} data-action="share/whatsapp/share">
                      <div className='bg-green-500 h-full w-full grid place-content-center text-white'>
                          <i className="fa-brands fa-whatsapp"></i>
                      </div>
                    </a>
                    <a target="_blank" className="block h-full w-full" href={`https://www.linkedin.com/shareArticle?mini=true&url=${process.env.REACT_APP_BASE_URL_NAME?.replaceAll(":", "%3A")}/blogs/${data.slug}`}>
                      <div className='bg-sky-500 h-full w-full grid place-content-center text-white'>
                          <i className="fa-brands fa-linkedin-in"></i>
                      </div>
                    </a>
                </div>
                <h3 className='my-6 text-2xl font-semibold'>Anda mungkin juga suka</h3>
                {
                  relatedBlogs.length === 0
                  ? <div className='w-full h-40 flex flex-col items-center justify-center text-gray-700'>
                    <i className="fa-regular fa-circle-question text-3xl"></i>
                    <h3 className='text-xl '>Tidak ada artikel terkait yang ditemukan</h3>
                  </div>
                  : <div className='grid grid-cols-1 md:grid-cols-2 gap-4 lg:grid-cols-1'>
                    { relatedBlogs.map((blog, index) => (
                      <Link to={`/blogs/${blog.slug}`} key={blog.id} className='lg:col-span-2 group bg-white shadow-around w-full lg:h-72 lg:auto-rows-[18rem] grid lg:grid-cols-2 overflow-hidden'>
                        <img src={resolveImgUrl(blog.imgUrl)} alt="" className='object-cover h-52 w-full lg:w-full lg:h-full transform group-hover:scale-110 transition-transform' />
                        <div className='flex flex-col justify-between p-5 z-[1] bg-white'>
                            <div className=' flex-col gap-y-2 flex'>
                                <span className='px-2 py-1 rounded-full text-sm w-max text-white' style={{backgroundColor : `#${blog.category.color}`}}>{blog.category.title}</span>
                                <h2 className='font-semibold text-2xl'>
                                    <TextTruncate
                                      text={blog.title}
                                      line={2}
                                      truncateText='...'
                                    />
                                </h2>
                                <TextTruncate
                                  text={blog.rawDescription}
                                  line={3}
                                  truncateText='...'
                                />
                            </div>
                            <div className='flex items-center gap-2 relative mt-3 lg:mt-0'>
                                <div className='w-6 h-6 rounded-full'>
                                  {
                                    !blog.submittedBy.profilePicture ?
                                    <div className='w-full h-full rounded-full bg-gray-500'></div>
                                    : <img src={resolveImgUrl(blog.submittedBy.profilePicture)} className='w-full h-full rounded-full object-cover' alt="" />
                                  }
                                </div>
                                <span className='text-xs'>{blog.submittedBy.name}</span>
                            </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                }
            </div>
            <div className='shadow-around h-max p-5 w-full sticky top-20 xl:block hidden'>
                <h3 className='text-xl font-semibold'>Artikel Terbaru</h3>
                <div className='flex flex-col gap-5 mt-3'>
                    {recentBlogs.map((blog)=><Link to={`/blogs/${blog.slug}`} key={blog.id} className='group text-sm grid grid-cols-2 gap-2'>
                        <div className='w-full h-20'>
                            <img src={resolveImgUrl(blog.imgUrl)} className="h-full w-full object-cover" alt="" />
                        </div>
                        <div className='h-full flex items-center'>
                          <p className='grow font-semibold group-hover:text-blue-500'>
                            <TextTruncate
                              text={blog.title}
                              line={3}
                              truncateText='...'
                            />
                          </p>
                        </div>
                    </Link>)}
                </div>
            </div>
        </Section>
    </div>
}

export default BlogShow